import React, { useEffect, useRef } from 'react';
import {
  Menu,
  IconLanguage,
  breakpoints,
  useTheme,
  createTheme,
  ThemeProvider,
} from '@fortum/elemental-ui';
import { Language } from '@configs/i18n';
import { useResponsiveWidth } from '@utils/useResponsiveWidth';
import { Namespace, useTranslation } from 'react-i18next';

interface LanguageMenuProps {
  languages: Language[];
  selected: Language;
  onSelect: (language: Language) => void;
  forceCondensed?: boolean;
  triggerColor?: string;
  transparent?: boolean;
}

const languageLabels: Record<Language, string> = {
  en: 'English (EN)',
  fi: 'Suomi (FI)',
  sv: 'Svenska (SV)',
};

export const LanguageMenu: React.FC<LanguageMenuProps> = ({
  languages,
  selected,
  onSelect,
  forceCondensed = false,
  triggerColor,
  transparent = false,
}) => {
  const navRef = useRef(null);
  const responsiveWidth = useResponsiveWidth();
  const isSmallScreen = responsiveWidth < breakpoints.l;
  const { t } = useTranslation<Namespace>('glossary');
  const shouldUseCondensed = forceCondensed || isSmallScreen;

  const baseTheme = useTheme();
  const theme = shouldUseCondensed
    ? createTheme({ variant: 'condensed' }, baseTheme)
    : baseTheme;

  const items = languages.map(lang => ({
    value: lang,
    name: languageLabels[lang],
  }));

  useEffect(() => {
    document.documentElement.lang = selected;
  }, [selected]);

  return (
    <ThemeProvider theme={theme}>
      <Menu
        aria-labelledby="language-menu-label"
        aria-label={t('language.selection')}
        icon={<IconLanguage color={triggerColor} />}
        items={items}
        selected={selected}
        onSelect={onSelect}
        label={selected.toUpperCase()}
        showLabel={!isSmallScreen}
        variant={shouldUseCondensed ? 'condensed' : 'normal'}
        buttonProps={
          triggerColor || transparent
            ? {
                color: triggerColor,
                variant: shouldUseCondensed ? 'condensed' : 'normal',
              }
            : undefined
        }
        id="language-menu"
        asDisclosure
        navigationRef={navRef}
      />
    </ThemeProvider>
  );
};
